import { useState } from "react";
import {
  Button,
  Card,
  Flex,
  OverlayStyle,
  QueryLoader,
  SlideoutDrawerV2,
  Text,
} from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { WorkspaceTable } from "~/pages/Dashboard/components/WorkspaceTable";
import Titlebar, { TitlebarAction } from "~/pages/UserAndRoles/components/Titlebar";
import { useAgGridUtils } from "~/pages/UserAndRoles/utils/gridUtils";
import { SPACING } from "~/styles/variables";
import { AddRole } from "./components/AddRole";
import { AssignToWorkspaceModal } from "./components/AssignToWorkspaceModal";
import { BulkArenaAccess } from "./components/BulkArenaAccess";
import { CanopyRoles } from "./components/CanopyRoles";
import { EllipsisButton } from "./components/CellComponents";
import { EditAccountSettingsModal } from "./components/EditAccountSettingsModal";
import { useEditAccountSettings } from "./components/EditAccountSettingsModal/hooks";
import RemoveUserModal from "./components/RemoveUserModal";
import { useArenaAccessForUser, useUserDetailsData } from "./hooks";
import { body, container } from "./styles_vanilla.css";

export const UserDetails = ({ userId, onClose }: { userId?: string; onClose: () => void }) => {
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isRemoveUserModalOpen, setIsRemoveUserModalOpen] = useState(false);
  const isBulkArenaAccessEnabled = useFeatureFlag("faBulkArenaAccess", false);

  const { userResponse, user, rolesRowData } = useUserDetailsData({
    userId,
  });
  const {
    rowData: workspaceAccesses,
    arenaAccessForUserQueryResult,
    hasBulkArenaAccess,
    bulkAccessRoleGroupName,
  } = useArenaAccessForUser(userId);
  const {
    onGridReady: onWorkspaceGridReady,
    resetFilters: resetWorkspaceFilters,
    isFilterPresent: isWorkspaceFilterPresent,
    onFilterChanged: onWorkspaceFilterChanged,
  } = useAgGridUtils();
  const {
    onGridReady: onCanopyRolesGridReady,
    resetFilters: resetCanopyRolesFilters,
    isFilterPresent: isCanopyRolesFilterPresent,
    onFilterChanged: onCanopyRolesFilterChanged,
  } = useAgGridUtils();

  const editAccountSettings = useEditAccountSettings({
    userId,
    currentEmailAddress: user.emailAddress || undefined,
  });

  const {
    showModal: showEditModal,
    onHide: onHideEditModal,
    openModal: openEditModal,
    initialValues: editInitialValues,
    onSubmit: onEditSubmit,
    isSubmitting: isEditSubmitting,
  } = editAccountSettings;

  const handleAssignToWorkspace = () => {
    setIsAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
  };

  const workspaceTableActions: TitlebarAction[] = [
    {
      show: isWorkspaceFilterPresent,
      buttonProps: {
        variant: "neutral",
        icon: "times",
        key: "clear_filters",
        onClick: resetWorkspaceFilters,
        children: "Clear filters",
      },
    },
    {
      show: true,
      buttonProps: {
        variant: "neutral",
        icon: "plus",
        key: "assign_to_workspace",
        onClick: handleAssignToWorkspace,
        children: "Assign to workspace",
      },
    },
  ];

  const canopyRolesTableActions: TitlebarAction[] = [
    {
      show: isCanopyRolesFilterPresent,
      buttonProps: {
        variant: "neutral",
        icon: "times",
        key: "clear_filters",
        onClick: resetCanopyRolesFilters,
        children: "Clear filters",
      },
    },
    {
      show: true,
      component: <AddRole key="add_role" userIds={[user.globalUserId || ""]} />,
    },
  ];

  return (
    <SlideoutDrawerV2
      isOpen={!!userId}
      onClose={onClose}
      overlayStyle={OverlayStyle.Transparent}
    >
      <SlideoutDrawerV2.ComplexHeader
        title={user.fullName || ""}
        subtitles={[<span key="1">{user.emailAddress}</span>]}
      />
      <SlideoutDrawerV2.Body className={body}>
        <Flex flexDirection="column" className={container}>
          <QueryLoader result={[userResponse, arenaAccessForUserQueryResult]}>
            <Flex flexDirection="column" gap={SPACING.LARGE}>
              <Card>
                <Flex flexDirection="column" gap={SPACING.MEDIUM}>
                  <Flex flexDirection="row" gap={SPACING.MEDIUM} justifyContent="space-between">
                    <Text variant="h3">Account settings</Text>
                    <Flex gap={SPACING.MEDIUM}>
                      <Button
                        variant="danger"
                        onClick={() => setIsRemoveUserModalOpen(true)}
                        icon="trash"
                      />
                      <Button variant="neutral" icon="edit" onClick={openEditModal}>
                        Edit
                      </Button>
                    </Flex>
                  </Flex>
                  <Flex flexDirection="row" gap={100}>
                    <Text variant="secondary-md">Email address</Text>
                    <Text variant="regular-md">{user.emailAddress}</Text>
                  </Flex>
                </Flex>
              </Card>
              <Card>
                <Flex flexDirection="column" gap={SPACING.MEDIUM}>
                  <Titlebar
                    title="Workspace access"
                    variant="h3"
                    actionButtons={workspaceTableActions}
                  />
                  {workspaceAccesses.length > 0 ? (
                    <WorkspaceTable
                      onGridReady={onWorkspaceGridReady}
                      arenaAccessRows={workspaceAccesses}
                      paginationPageSizeSelector={[3, 10, 20]}
                      paginationPageSize={3}
                      FinalElement={EllipsisButton}
                      finalColumnText=""
                      cellRendererParams={{
                        userInfo: { userName: user.fullName || "", userId: userId || "" },
                      }}
                      domLayout="autoHeight"
                      onFilterChanged={onWorkspaceFilterChanged}
                    />
                  ) : (
                    <Text variant="regular-md" marginBottom="md">
                      This user has not been added to any workspaces
                    </Text>
                  )}
                  {isBulkArenaAccessEnabled && (
                    <BulkArenaAccess
                      userId={userId ?? ""}
                      hasBulkArenaAccess={hasBulkArenaAccess}
                      roleGroupName={bulkAccessRoleGroupName}
                    />
                  )}
                </Flex>
              </Card>
              <Card>
                <Flex flexDirection="column" gap={SPACING.MEDIUM}>
                  <Titlebar
                    title="Canopy roles"
                    variant="h3"
                    actionButtons={canopyRolesTableActions}
                  />
                  {rolesRowData.length > 0 ? (
                    <CanopyRoles
                      onGridReady={onCanopyRolesGridReady}
                      rowData={rolesRowData}
                      onFilterChanged={onCanopyRolesFilterChanged}
                    />
                  ) : (
                    <Text variant="regular-md" marginBottom="md">
                      This user has not been assigned to any Canopy roles
                    </Text>
                  )}
                </Flex>
              </Card>
            </Flex>
          </QueryLoader>
        </Flex>
      </SlideoutDrawerV2.Body>
      <AssignToWorkspaceModal
        isOpen={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        userId={userId || ""}
        userName={user.fullName || "user"}
        existingAssignments={workspaceAccesses}
      />
      <EditAccountSettingsModal
        show={showEditModal}
        onHide={onHideEditModal}
        initialValues={editInitialValues}
        onSubmit={onEditSubmit}
        isSubmitting={isEditSubmitting}
      />
      <RemoveUserModal
        userData={{
          id: userId || "",
          name: user.fullName || "",
          email: user.emailAddress || "",
        }}
        isOpen={isRemoveUserModalOpen}
        onClose={() => setIsRemoveUserModalOpen(false)}
        closeDrawer={onClose}
      />
    </SlideoutDrawerV2>
  );
};
